import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type {
  CustomerCrmSummary,
  CustomerDirectoryItem,
  Localized,
} from '@servicesai/api-client';
import {
  DataTable,
  EmptyState,
  FilterBar,
  GlassButton,
  GlassCard,
  GlassInput,
  GlassSelect,
  StatusPill,
  formatDate,
  useI18n,
  type DataTableColumn,
} from '@servicesai/ui';
import { admin } from '../lib/api.js';
import { S } from '../strings.js';
import { HeroBand, MetricTile, MOTION, SectionCard } from '../components/elevated/kit.js';
import { CxoneBadge, CxoneCrmBanner } from '../components/CxoneBrand.js';
import { SECTION_ACCENTS } from '../lib/accents.js';

type DirectoryView = 'table' | 'pipeline';

const TIER_LABEL: Record<string, Localized> = {
  anonymous: { en: 'Anonymous', ar: 'مجهول' },
  phone_otp: { en: 'Phone verified', ar: 'هاتف متحقق' },
  uaepass_basic: { en: 'UAE Pass', ar: 'الهوية الرقمية' },
  uaepass_verified: { en: 'UAE Pass verified', ar: 'هوية رقمية موثقة' },
};

/* Stage accents come from the console accent registry (plan point 35). */
const LIFECYCLE: {
  id: CustomerDirectoryItem['lifecycleStage'];
  label: Localized;
  accent: string;
}[] = [
  { id: 'new', label: { en: 'New', ar: 'جديد' }, accent: SECTION_ACCENTS.assurance },
  { id: 'active', label: { en: 'Active', ar: 'نشط' }, accent: SECTION_ACCENTS.engagement },
  { id: 'at_risk', label: { en: 'At risk', ar: 'معرض للخطر' }, accent: SECTION_ACCENTS.money },
  { id: 'vip', label: { en: 'VIP', ar: 'مميز' }, accent: SECTION_ACCENTS.platform },
  { id: 'inactive', label: { en: 'Inactive', ar: 'غير نشط' }, accent: SECTION_ACCENTS.night },
];

function healthScore(customer: CustomerDirectoryItem): number {
  const satisfaction =
    customer.satisfactionAverage === null
      ? 0
      : Math.round((customer.satisfactionAverage - 3) * 8);
  return Math.max(
    0,
    Math.min(
      100,
      92 -
        customer.activeTickets * 6 -
        customer.openTasks * 4 -
        customer.openComplaints * 18 -
        (customer.riskLevel === 'critical'
          ? 30
          : customer.riskLevel === 'elevated'
            ? 15
            : 0) +
        satisfaction,
    ),
  );
}

function scoreTone(score: number): string {
  return score >= 80 ? 'bg-approve' : score >= 55 ? 'bg-review' : 'bg-reject';
}

function initials(name: string | null): string {
  if (!name) return '—';
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
}

export function CustomersPage() {
  const { l, lang } = useI18n();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [items, setItems] = useState<CustomerDirectoryItem[] | null>(null);
  const [summary, setSummary] = useState<CustomerCrmSummary | null>(null);
  const [error, setError] = useState(false);
  const [view, setView] = useState<DirectoryView>('table');
  const [lifecycle, setLifecycle] = useState('');
  const [risk, setRisk] = useState('');
  const [verification, setVerification] = useState('');
  const [owner, setOwner] = useState('');

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setError(false);
      admin()
        .customers(q || undefined)
        .then((response) => {
          setItems(response.items);
          setSummary(response.summary);
        })
        .catch(() => setError(true));
    }, 250);
    return () => window.clearTimeout(timer);
  }, [q]);

  const owners = [...new Set((items ?? []).map((item) => item.ownerName).filter(Boolean))].sort() as string[];
  const filtered = (items ?? []).filter(
    (customer) =>
      (!lifecycle || customer.lifecycleStage === lifecycle) &&
      (!risk || customer.riskLevel === risk) &&
      (!verification || customer.verificationTier === verification) &&
      (!owner ||
        (owner === '__unassigned__'
          ? customer.ownerName === null
          : customer.ownerName === owner)),
  );

  const columns: DataTableColumn<CustomerDirectoryItem>[] = [
    {
      id: 'customer',
      header: l({ en: 'Customer', ar: 'المتعامل' }),
      sortable: true,
      value: (customer) => customer.name,
      render: (customer) => (
        <div className="flex min-w-64 items-center gap-3">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-aeblack-800 text-xs font-black text-white">
            {initials(customer.name)}
          </span>
          <div className="min-w-0">
            <div className="max-w-64 truncate font-bold">
              {customer.name ?? l({ en: 'Unnamed customer', ar: 'متعامل بلا اسم' })}
            </div>
            <div className="mt-0.5 font-mono text-[10px] text-ink-faint" dir="ltr">
              {customer.phone ?? customer.id.slice(0, 12)}
            </div>
            {customer.tags.length > 0 && (
              <div className="mt-1 flex max-w-64 flex-wrap gap-1">
                {customer.tags.slice(0, 3).map((tag) => (
                  <span key={tag} className="rounded bg-aeblack-50 px-1.5 py-0.5 text-[9px] font-semibold text-ink-faint">
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      id: 'relationship',
      header: l({ en: 'Relationship', ar: 'العلاقة' }),
      sortable: true,
      value: (customer) => customer.lifecycleStage,
      render: (customer) => (
        <div className="space-y-1">
          <StatusPill
            state={
              customer.lifecycleStage === 'at_risk'
                ? 'in_review'
                : customer.lifecycleStage === 'inactive'
                  ? 'rejected'
                  : 'published'
            }
            label={customer.lifecycleStage.replaceAll('_', ' ')}
          />
          <div className="text-[10px] text-ink-faint">{customer.riskLevel} risk</div>
        </div>
      ),
    },
    {
      id: 'health',
      header: l({ en: 'Health', ar: 'الصحة' }),
      sortable: true,
      value: healthScore,
      render: (customer) => {
        const score = healthScore(customer);
        return (
          <div className="w-28">
            <div className="mb-1 flex justify-between text-[10px]">
              <span className="font-bold">{score}%</span>
              <span className="text-ink-faint">
                {score >= 80 ? l({ en: 'Healthy', ar: 'جيد' }) : score >= 55 ? l({ en: 'Watch', ar: 'مراقبة' }) : l({ en: 'Action', ar: 'إجراء' })}
              </span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-aeblack-100">
              <div className={`h-full rounded-full ${scoreTone(score)}`} style={{ width: `${score}%` }} />
            </div>
          </div>
        );
      },
    },
    {
      id: 'owner',
      header: l({ en: 'CRM owner', ar: 'مسؤول العلاقة' }),
      sortable: true,
      value: (customer) => customer.ownerName,
      render: (customer) => (
        <div>
          <div className="font-semibold">
            {customer.ownerName ?? l({ en: 'Unassigned', ar: 'غير معيّن' })}
          </div>
          {customer.nextFollowUpAt && (
            <div className={`mt-0.5 text-[10px] ${
              new Date(customer.nextFollowUpAt) <= new Date()
                ? 'font-bold text-reject'
                : 'text-ink-faint'
            }`}>
              {l({ en: 'Follow-up', ar: 'متابعة' })}: {formatDate(customer.nextFollowUpAt, lang)}
            </div>
          )}
        </div>
      ),
    },
    {
      id: 'work',
      header: l({ en: 'Open work', ar: 'العمل المفتوح' }),
      sortable: true,
      value: (customer) => customer.activeTickets + customer.openTasks,
      render: (customer) => (
        <div className="grid grid-cols-3 gap-2 text-center">
          <CountCell value={customer.activeTickets} label={l({ en: 'tickets', ar: 'تذاكر' })} alert={customer.activeTickets > 0} />
          <CountCell value={customer.openTasks} label={l({ en: 'tasks', ar: 'مهام' })} alert={customer.openTasks > 0} />
          <CountCell value={customer.openComplaints} label={l({ en: 'issues', ar: 'شكاوى' })} alert={customer.openComplaints > 0} danger />
        </div>
      ),
    },
    {
      id: 'engagement',
      header: l({ en: 'Engagement', ar: 'التفاعل' }),
      sortable: true,
      value: (customer) => customer.conversations,
      render: (customer) => (
        <div className="text-xs">
          <div><strong className="font-mono">{customer.conversations}</strong> {l({ en: 'conversations', ar: 'محادثات' })}</div>
          <div className="mt-0.5 text-ink-faint">
            {customer.satisfactionAverage === null
              ? l({ en: 'No CSAT', ar: 'لا يوجد رضا' })
              : `${customer.satisfactionAverage.toFixed(1)}/5 CSAT`}
            {' · '}
            {customer.notes} {l({ en: 'notes', ar: 'ملاحظات' })}
          </div>
          {customer.cxoneConversations > 0 && (
            <CxoneBadge
              status="live"
              label={<span className="font-mono text-[9px] font-bold">{customer.cxoneConversations}</span>}
              className="mt-1.5"
            />
          )}
        </div>
      ),
    },
    {
      id: 'verification',
      header: l({ en: 'Identity', ar: 'الهوية' }),
      sortable: true,
      value: (customer) => customer.verificationTier,
      render: (customer) => (
        <StatusPill
          state={customer.verificationTier.startsWith('uaepass') ? 'published' : 'in_progress'}
          label={l(TIER_LABEL[customer.verificationTier] ?? { en: customer.verificationTier, ar: customer.verificationTier })}
        />
      ),
    },
    {
      id: 'activity',
      header: l({ en: 'Last activity', ar: 'آخر نشاط' }),
      sortable: true,
      value: (customer) => customer.lastActivityAt,
      render: (customer) => (
        <div className="text-xs">
          <div>{formatDate(customer.lastActivityAt, lang)}</div>
          <div className="mt-0.5 font-mono text-[10px] uppercase text-ink-faint">{customer.lang}</div>
        </div>
      ),
    },
    {
      id: 'action',
      header: '',
      align: 'end',
      render: () => (
        <span className="text-xs font-bold text-gold-deep">
          {l(S.openProfile)} →
        </span>
      ),
    },
  ];

  return (
    <div>
      <HeroBand
        accent="#0173ab"
        eyebrow={l({ en: 'Customer 360 registry', ar: 'سجل المتعامل 360' })}
        title={l({ en: 'Customer relationship management', ar: 'إدارة علاقات المتعامل' })}
        subtitle={l({
          en: 'A live, access-logged view of every relationship, open commitment, conversation and service outcome.',
          ar: 'عرض مباشر ومسجل للوصول لكل علاقة والتزام مفتوح ومحادثة ونتيجة خدمة.',
        })}
        status={
          <span className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-[10px] font-extrabold uppercase tracking-wide text-white/80">
            {l({ en: 'PDPL access logged', ar: 'الوصول مسجل وفق حماية البيانات' })}
          </span>
        }
        actions={
          <button
            type="button"
            onClick={() => window.print()}
            className="inline-flex min-h-10 cursor-pointer items-center rounded-xl bg-aegold-500 px-4 text-xs font-extrabold text-aeblack-900 shadow-lg transition hover:brightness-110"
          >
            {l({ en: 'Print CRM snapshot', ar: 'طباعة لقطة CRM' })}
          </button>
        }
        metrics={
          summary ? (
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-6">
              <MetricTile
                dark
                label={l({ en: 'Customers', ar: 'المتعاملون' })}
                value={summary.total}
                hint={`+${summary.newLast30Days} ${l({ en: 'in 30 days', ar: 'خلال 30 يوماً' })}`}
              />
              <MetricTile
                dark
                label={l({ en: 'UAE Pass verified', ar: 'موثقون بالهوية الرقمية' })}
                value={summary.verified}
                hint={
                  summary.total
                    ? `${Math.round((summary.verified / summary.total) * 100)}%`
                    : undefined
                }
                accent="#3f8e50"
              />
              <MetricTile
                dark
                label={l({ en: 'Active ticket holders', ar: 'أصحاب التذاكر النشطة' })}
                value={summary.activeTickets}
                accent={summary.activeTickets ? '#d67907' : '#3f8e50'}
              />
              <MetricTile
                dark
                label={l({ en: 'Open follow-ups', ar: 'المتابعات المفتوحة' })}
                value={summary.openTasks}
                hint={`${summary.followUpsDue} ${l({ en: 'due', ar: 'مستحقة' })}`}
                accent={summary.followUpsDue ? '#d83731' : '#cba344'}
              />
              <MetricTile
                dark
                label={l({ en: 'At risk / VIP', ar: 'معرضون للخطر / مميزون' })}
                value={`${summary.atRisk} / ${summary.vip}`}
                hint={`${summary.unassigned} ${l({ en: 'unassigned', ar: 'غير معيّنين' })}`}
                accent={summary.atRisk ? '#d83731' : '#cba344'}
              />
              <MetricTile
                dark
                label={l({ en: 'Customer pulse', ar: 'نبض المتعامل' })}
                value={
                  summary.satisfactionAverage === null
                    ? '—'
                    : `${summary.satisfactionAverage.toFixed(1)}/5`
                }
                hint={`${summary.conversationsLast30Days} ${l({ en: 'conversations (30d)', ar: 'محادثات (30 يوماً)' })}`}
                accent={
                  summary.satisfactionAverage !== null && summary.satisfactionAverage < 3
                    ? '#d83731'
                    : '#3f8e50'
                }
              />
            </div>
          ) : undefined
        }
      />

      {summary && (
        <div className="mb-4">
          <CxoneCrmBanner
            conversationCount={summary.cxoneConversations}
            onOpenQueue={() => navigate('/handover')}
          />
        </div>
      )}

      <SectionCard
        className="mb-4"
        padded={false}
        accent="#0173ab"
        title={l({ en: 'Relationship command center', ar: 'مركز قيادة العلاقات' })}
        hint={`${filtered.length} ${l({ en: 'customer relationships in view', ar: 'علاقة متعامل في العرض' })} · ${l({ en: 'Prioritized by recent activity, risk and open commitments.', ar: 'مرتبة حسب النشاط الحديث والمخاطر والالتزامات المفتوحة.' })}`}
        actions={
          <div className="flex rounded-lg border border-aeblack-150 bg-aeblack-50 p-1">
            <ViewButton active={view === 'table'} onClick={() => setView('table')} label={l({ en: 'Table', ar: 'جدول' })} />
            <ViewButton active={view === 'pipeline'} onClick={() => setView('pipeline')} label={l({ en: 'Lifecycle', ar: 'دورة العلاقة' })} />
          </div>
        }
      >
        <FilterBar className="!mb-0 !rounded-none !border-0 !p-4" summary={`${filtered.length} / ${items?.length ?? 0}`}>
          <GlassInput
            value={q}
            onChange={(event) => setQ(event.target.value)}
            placeholder={l(S.customerSearch)}
            className="w-full sm:w-80"
          />
          <GlassSelect value={lifecycle} onChange={(event) => setLifecycle(event.target.value)}>
            <option value="">{l({ en: 'All lifecycle stages', ar: 'كل مراحل العلاقة' })}</option>
            {LIFECYCLE.map((stage) => <option key={stage.id} value={stage.id}>{l(stage.label)}</option>)}
          </GlassSelect>
          <GlassSelect value={risk} onChange={(event) => setRisk(event.target.value)}>
            <option value="">{l({ en: 'All risk levels', ar: 'كل مستويات المخاطر' })}</option>
            {['low', 'standard', 'elevated', 'critical'].map((value) => <option key={value}>{value}</option>)}
          </GlassSelect>
          <GlassSelect value={verification} onChange={(event) => setVerification(event.target.value)}>
            <option value="">{l({ en: 'All identity tiers', ar: 'كل مستويات الهوية' })}</option>
            {Object.keys(TIER_LABEL).map((value) => <option key={value}>{l(TIER_LABEL[value]!)}</option>)}
          </GlassSelect>
          <GlassSelect value={owner} onChange={(event) => setOwner(event.target.value)}>
            <option value="">{l({ en: 'All CRM owners', ar: 'كل مسؤولي العلاقات' })}</option>
            <option value="__unassigned__">{l({ en: 'Unassigned only', ar: 'غير معيّنين فقط' })}</option>
            {owners.map((value) => <option key={value}>{value}</option>)}
          </GlassSelect>
          {(lifecycle || risk || verification || owner) && (
            <GlassButton
              size="sm"
              variant="ghost"
              onClick={() => {
                setLifecycle('');
                setRisk('');
                setVerification('');
                setOwner('');
              }}
            >
              {l({ en: 'Clear filters', ar: 'مسح عوامل التصفية' })}
            </GlassButton>
          )}
        </FilterBar>
      </SectionCard>

      {error && (
        <div role="alert" className="aegov-alert alert-error">
          <div className="alert-content text-sm">{l(S.loadFailed)}</div>
        </div>
      )}

      {!error && view === 'table' && (
        <DataTable
          rows={filtered}
          columns={columns}
          rowKey={(customer) => customer.id}
          loading={items === null}
          emptyTitle={l(S.noCustomers)}
          emptyHint={l(S.noCustomersHint)}
          filename="customer-crm-directory.csv"
          onRowClick={(customer) => navigate(`/customers/${customer.id}`)}
          pageSize={30}
        />
      )}

      {!error && view === 'pipeline' && (
        <LifecyclePipeline
          rows={filtered}
          loading={items === null}
          onOpen={(customer) => navigate(`/customers/${customer.id}`)}
        />
      )}
    </div>
  );
}

function CountCell({
  value,
  label,
  alert,
  danger,
}: {
  value: number;
  label: string;
  alert?: boolean;
  danger?: boolean;
}) {
  return (
    <div>
      <div
        className={`font-mono text-sm font-extrabold ${
          alert ? (danger ? 'text-reject' : 'text-gold-deep') : 'text-ink'
        }`}
      >
        {value}
      </div>
      <div className="text-[9px] text-ink-faint">{label}</div>
    </div>
  );
}

function ViewButton({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`inline-flex min-h-8 items-center rounded-md px-3 text-xs font-bold transition ${
        active ? 'bg-aeblack-900 text-white shadow-sm' : 'text-ink-soft hover:bg-white hover:text-ink'
      }`}
    >
      {label}
    </button>
  );
}

function LifecyclePipeline({
  rows,
  loading,
  onOpen,
}: {
  rows: CustomerDirectoryItem[];
  loading: boolean;
  onOpen: (customer: CustomerDirectoryItem) => void;
}) {
  const { l, lang } = useI18n();
  if (loading) {
    return <div className="h-96 animate-pulse rounded-xl border border-aeblack-100 bg-white" />;
  }
  if (rows.length === 0) {
    return (
      <GlassCard>
        <EmptyState title={l(S.noCustomers)} hint={l(S.noCustomersHint)} />
      </GlassCard>
    );
  }
  const maxStageCount = Math.max(
    1,
    ...LIFECYCLE.map((stage) => rows.filter((customer) => customer.lifecycleStage === stage.id).length),
  );
  return (
    <div className="overflow-x-auto pb-2">
      <div className="grid min-w-[82rem] grid-cols-5 gap-3">
        {LIFECYCLE.map((stage) => {
          const customers = rows.filter((customer) => customer.lifecycleStage === stage.id);
          return (
            <section
              key={stage.id}
              className="overflow-hidden rounded-xl border border-aeblack-100 bg-aeblack-50/70 transition-all"
              style={{ transitionDuration: `${MOTION.base}ms`, transitionTimingFunction: MOTION.ease }}
            >
              <header className="border-b border-aeblack-100 bg-white px-3 py-3">
                <div className="flex items-center gap-2">
                  <span aria-hidden className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: stage.accent }} />
                  <h2 className="text-xs font-extrabold uppercase tracking-wide">{l(stage.label)}</h2>
                  <span
                    className="ms-auto rounded-full px-2 py-0.5 font-mono text-[10px] font-extrabold"
                    style={{ backgroundColor: `${stage.accent}1f`, color: stage.accent }}
                  >
                    {customers.length}
                  </span>
                </div>
                <div className="mt-2 h-1 overflow-hidden rounded-full bg-aeblack-100" aria-hidden>
                  <div
                    className="h-full rounded-full transition-[width]"
                    style={{
                      width: `${(customers.length / maxStageCount) * 100}%`,
                      backgroundColor: stage.accent,
                      transitionDuration: `${MOTION.slow}ms`,
                      transitionTimingFunction: MOTION.ease,
                    }}
                  />
                </div>
              </header>
              <div className="max-h-[52rem] space-y-2 overflow-y-auto p-2">
                {customers.map((customer) => {
                  const score = healthScore(customer);
                  return (
                    <button
                      key={customer.id}
                      type="button"
                      onClick={() => onOpen(customer)}
                      className="w-full rounded-lg border border-aeblack-100 bg-white p-3 text-start shadow-sm transition hover:-translate-y-0.5 hover:border-aegold-300 hover:shadow-md"
                    >
                      <div className="flex items-center gap-2.5">
                        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-aeblack-800 text-[10px] font-black text-white">
                          {initials(customer.name)}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-xs font-extrabold">
                            {customer.name ?? l({ en: 'Unnamed customer', ar: 'متعامل بلا اسم' })}
                          </div>
                          <div className="truncate font-mono text-[9px] text-ink-faint" dir="ltr">
                            {customer.phone ?? customer.id.slice(0, 10)}
                          </div>
                        </div>
                      </div>
                      <div className="mt-3 flex items-center gap-2">
                        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-aeblack-100">
                          <div className={`h-full ${scoreTone(score)}`} style={{ width: `${score}%` }} />
                        </div>
                        <span className="font-mono text-[10px] font-bold">{score}%</span>
                      </div>
                      <div className="mt-3 grid grid-cols-3 gap-1 border-t border-aeblack-50 pt-2 text-center">
                        <CountCell value={customer.activeTickets} label={l({ en: 'tickets', ar: 'تذاكر' })} alert={customer.activeTickets > 0} />
                        <CountCell value={customer.openTasks} label={l({ en: 'tasks', ar: 'مهام' })} alert={customer.openTasks > 0} />
                        <CountCell value={customer.openComplaints} label={l({ en: 'issues', ar: 'شكاوى' })} alert={customer.openComplaints > 0} danger />
                      </div>
                      {customer.cxoneConversations > 0 && (
                        <CxoneBadge
                          status="live"
                          label={
                            <span className="font-mono text-[9px] font-bold">
                              {customer.cxoneConversations}
                            </span>
                          }
                          className="mt-2"
                        />
                      )}
                      <div className="mt-2 space-y-0.5 border-t border-aeblack-50 pt-2 text-[9px] text-ink-faint">
                        <div className="flex items-center justify-between gap-2">
                          <span>{l(S.lastActivity)}</span>
                          <span className="shrink-0">{formatDate(customer.lastActivityAt, lang)}</span>
                        </div>
                        <div className="truncate">
                          {customer.ownerName ?? l({ en: 'Unassigned', ar: 'غير معيّن' })}
                        </div>
                      </div>
                    </button>
                  );
                })}
                {customers.length === 0 && (
                  <div className="py-8 text-center text-[10px] text-ink-faint">
                    {l({ en: 'No customers', ar: 'لا يوجد متعاملون' })}
                  </div>
                )}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
